import React from 'react';
import { useSelector } from 'react-redux';
import { Hidden } from '@material-ui/core';
import { DividerSection } from '../../../global/components/layout/divider-section';
import { TcsmStates } from '../../../root/tcsm-model';
import { ClergyBioSettings } from '../../arc/models/arc-epi-props';
import { DataState, EcclesiaticDetail, Ecclesiatics } from '../model/clergy-bio-app-models';
import ClergyBioComponentLoader from '../components/clergy-bio-component-loader';
import { sortTableStyles } from '../../../global/constants/theme/styles/tables/generic-sort-table';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import { ClericIcon } from '../../account-info/components/cleric-indicator';
import { headerIconStyles } from '../../../global/constants/theme/styles/components/header-icon-styles';
import { getFormattedDate } from '../../../global/helpers/date-helpers';
import { WysiwygWithReactLink } from '../../../global/components/wysiwyg-with-react-link';



interface Props {
	settings: ClergyBioSettings;
}

function ClergyBioEcclesiastical(props: Props) {
	const tableClasses = sortTableStyles();
	const headerClasses = headerIconStyles();

	const { settings } = props;
	const loadingState = useSelector<TcsmStates, DataState>(state => state?.clergyBioState?.loaded?.ecclesiastics);
	const ecclesiastical = useSelector<TcsmStates, Ecclesiatics>(state => state?.clergyBioState?.ecclesiastics);
	function Labels(consecrator?: boolean) {
		return (
			<Hidden xsDown>
				<div className='sort-table-row'>
					<div className='sort-table-row-cell'><p><strong>{settings?.dateLabel}</strong></p></div>
					<div className='sort-table-row-cell'><p><strong>{settings?.byBishopLabel}</strong></p></div>
					{consecrator && <div className='sort-table-row-cell'><p><strong>{settings?.coConsecratorsLabel}</strong></p></div>}
					<div className='sort-table-row-cell'><p><strong>{settings?.forLabel}</strong></p></div>
				</div>
			</Hidden>
		);
	}

	function RowItemNoInformation() {
		return (
			<div className='sort-table-row'>
				<div className='sort-table-row-cell'>
					<WysiwygWithReactLink content={settings?.noInformationText} />
				</div>
			</div>);
	}

	function RowItemForOnly(ecclesiaticDetail: EcclesiaticDetail) {
		return (
			<div className='sort-table-row'>
				<div className='sort-table-row-cell'>
					<p>{ecclesiaticDetail?.for}</p>
				</div>
			</div>);
	}

	function RowItem(ecclesiaticDetail: EcclesiaticDetail, consecrator?: boolean) {
		return (
			<div className='sort-table-row'>
				<div className='sort-table-row-cell'>
					<Hidden smUp>
						<p><strong>{settings?.dateLabel}</strong></p>
					</Hidden>
					<p>{getFormattedDate(ecclesiaticDetail?.date)}</p>
				</div>
				<div className='sort-table-row-cell'>
					<Hidden smUp>
						<p><strong>{settings?.byBishopLabel}</strong></p>
					</Hidden>
					<p>{ecclesiaticDetail?.by}</p></div>
				{consecrator && <div className='sort-table-row-cell'>
					<Hidden smUp>
						<p><strong>{settings?.coConsecratorsLabel}</strong></p>
					</Hidden>
					<p>{ecclesiaticDetail?.coConsecrators?.map(c => c?.by).join(',')}</p></div>}
				<div className='sort-table-row-cell'>
					<Hidden smUp>
						<p><strong>{settings?.forLabel}</strong></p>
					</Hidden>
					<p>{ecclesiaticDetail?.for}</p></div>
			</div>);
	}

	function renderContent() {
		return (
			<>
				<ContentSpacer>
					<div className={headerClasses.iconHeader}>
						<ClericIcon />
						<h3>{settings?.canonicalResidence}</h3>
					</div>					
					<div className={tableClasses.sortTable}>						
						<div className='sort-table-content'>
							{ ecclesiastical?.canonical?.details?.for ? RowItemForOnly(ecclesiastical?.canonical?.details) : RowItemNoInformation() }
						</div>
					</div>
				</ContentSpacer>
				<ContentSpacer>
					<div className={headerClasses.iconHeader}>
						<ClericIcon />
						<h3>{settings?.diaconateLabel}</h3>
					</div>					
					<div className={tableClasses.sortTable}>
						{Labels()}
						<div className='sort-table-content'>
							{ ecclesiastical?.diaconate?.details?.for ? RowItem(ecclesiastical?.diaconate?.details) : RowItemNoInformation() }
						</div>
					</div>
				</ContentSpacer>
				<ContentSpacer>
					<div className={headerClasses.iconHeader}>
						<ClericIcon />
						<h3>{settings?.priesthoodLabel}</h3>
					</div>					
					<div className={tableClasses.sortTable}>
						{Labels()}
						<div className='sort-table-content'>
							{ ecclesiastical?.priesthood?.details?.for ? RowItem(ecclesiastical?.priesthood?.details) : RowItemNoInformation() }
						</div>
					</div>
				</ContentSpacer>
				<ContentSpacer>
					<div className={headerClasses.iconHeader}>
						<ClericIcon />
						<h3>{settings?.receptionLabel}</h3>
					</div>
					<div className={tableClasses.sortTable}>
						{Labels()}
						<div className='sort-table-content'>
							{ ecclesiastical?.reception?.details?.for ? RowItem(ecclesiastical?.reception?.details) : RowItemNoInformation() }
						</div>
					</div>
				</ContentSpacer>
				<ContentSpacer>
					<div className={headerClasses.iconHeader}>
						<ClericIcon />
						<h3>{settings?.transferLabel}</h3>
					</div>					
					<div className={tableClasses.sortTable}>
						{Labels()}
						<div className='sort-table-content'>							
							{ ecclesiastical?.transfer?.details?.for ? RowItem(ecclesiastical?.transfer?.details) : RowItemNoInformation()}
						</div>
					</div>
				</ContentSpacer>
				<ContentSpacer>
					<div className={headerClasses.iconHeader}>
						<ClericIcon />
						<h3>{settings?.consecrationLabel}</h3>
					</div>					
					<div className={tableClasses.sortTable}>
						{Labels(true)}
						<div className='sort-table-content'>
							{ ecclesiastical?.consecration?.details?.for ? RowItem(ecclesiastical?.consecration?.details, true) : RowItemNoInformation()}
						</div>
					</div> 
				</ContentSpacer>
			</>
		);
	}

	return (
		<DividerSection sectionText={settings?.ecclesiasticalTitle} >
			<ClergyBioComponentLoader
				state={loadingState}
				content={renderContent()}
			/>
		</DividerSection>
	);
}


export default ClergyBioEcclesiastical;